import { ElementNotFound } from "@senciamatch/frontend/components/ElementNotFound";
import { FullScreenLoading } from "@senciamatch/frontend/components/FullScreenLoading";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import type { EditStaffInput } from "@senciamatch/shared/models/staff";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

export const StaffEditPage = () => {
  const { id } = useParams();
  const { register, handleSubmit, setValue } = useForm<EditStaffInput>();
  const getStaff = trpc.getStaff.useQuery({ id: id as string });
  const updateStaff = trpc.updateStaff.useMutation();

  useEffect(() => {
    if (getStaff.data?.staff) {
      setValue("canWorkAtNight", getStaff.data.staff.canWorkAtNight);
      setValue("isActive", getStaff.data.staff.isActive);
      setValue("isDisasterCenter", getStaff.data.staff.isDisasterCenter);
    }
  }, [getStaff.data, setValue]);

  if (getStaff.isPending) return <FullScreenLoading />;
  if (!getStaff.data?.staff) return <ElementNotFound />;

  return (
    <div className="h-screen flex flex-col">
      <h2 className="text-2xl font-bold py-10 flex-shrink-0">スタッフ編集</h2>
      <div className="flex-grow flex flex-col bg-white border border-base-100 px-8 max-w-[600px]">
        <div className="flex flex-col gap-8 mt-8 border-b border-gray-200 pb-8">
          <h2 className="text-lg font-bold mb-4">基本情報</h2>
          <div className="flex flex-col gap-2">
            <span className="text-sm text-gray-900 font-bold">氏名</span>
            <p className="text-sm text-gray-500">{getStaff.data.staff.name}</p>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm text-gray-900 font-bold">氏名（カナ）</span>
            <p className="text-sm text-gray-500">{getStaff.data.staff.nameKana}</p>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm text-gray-900 font-bold">住所</span>
            <p className="text-sm text-gray-500">
              {getStaff.data.staff.postalCode} <br />
              {getStaff.data.staff.address}
            </p>
          </div>

          {getStaff.data.staff.nearestStation && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-gray-900 font-bold">最寄駅</span>
              <p className="text-sm text-gray-500">{getStaff.data.staff.nearestStation}</p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-sm text-gray-900 font-bold">社会保険</span>
            <p className="text-sm text-gray-500">{getStaff.data.staff.isSocialInsurance ? "加入" : "未加入"}</p>
          </div>
        </div>
        <form
          onSubmit={handleSubmit((data) => {
            if (!id) return;
            updateStaff.mutate(
              {
                id: id || "",
                canWorkAtNight: data.canWorkAtNight ?? false,
                isDisasterCenter: data.isDisasterCenter ?? false,
                isActive: data.isActive ?? false,
              },
              {
                onSuccess: () => {
                  getStaff.refetch();
                  alert("更新しました");
                },
              },
            );
          })}
          className="flex flex-col gap-8 mt-8"
        >
          <div className="flex flex-col gap-2">
            <label htmlFor="canWorkAtNight" className="text-sm text-gray-900 font-bold">
              全日勤務提出時の深夜勤務可否
            </label>
            <div className="flex items-center gap-2">
              <input
                id="canWorkAtNight"
                type="checkbox"
                className="border border-gray-300 rounded-md p-2"
                {...register("canWorkAtNight")}
              />
              <span className="text-sm text-gray-500">可能</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="isDisasterCenter" className="text-sm text-gray-900 font-bold">
              防災センター要員
            </label>
            <div className="flex items-center gap-2">
              <input
                id="isDisasterCenter"
                type="checkbox"
                className="border border-gray-300 rounded-md p-2"
                {...register("isDisasterCenter")}
              />
              <span className="text-sm text-gray-500">要員</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="isActive" className="text-sm text-gray-900 font-bold">
              アクティブ
            </label>
            <div className="flex items-center gap-2">
              <input
                id="isActive"
                type="checkbox"
                className="border border-gray-300 rounded-md p-2"
                {...register("isActive")}
              />
              <span className="text-sm text-gray-500">アクティブ</span>
            </div>
          </div>

          <button type="submit" className="w-full bg-[#00AF31] text-white p-2 rounded-md">
            保存
          </button>
        </form>
      </div>
    </div>
  );
};
